import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SpeedIcon from '@mui/icons-material/Speed';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { productionKeys } from './queryKeys';
import {
  listStandards,
  upsertStandard,
  updateStandard,
  deleteStandard,
} from './productionApi';
import type { ProductionStandard, StandardScopeType } from './productionTypes';
import { formatDuration } from './productionUtils';
import { PRESS_ORDER, pressRank } from './board/ganttConfig';

// React Query key for the all-presses standards fetch. Standards are filtered
// to a press client-side, so a single cached list serves every press view.
const STANDARDS_QUERY_KEY = productionKeys.standards('__all__');
const DEFAULT_PRESS = PRESS_ORDER[0] ?? 'P15';

type ScopeFilter = 'all' | StandardScopeType;
type PressFilter = 'all' | string;

function scopeChipColour(
  scope: StandardScopeType,
): 'primary' | 'info' | 'warning' | 'secondary' | 'success' | 'default' {
  if (scope === 'default') return 'primary';
  if (scope === 'forme') return 'secondary';
  if (scope === 'stock_code') return 'info';
  if (scope === 'substrate') return 'warning';
  if (scope === 'customer') return 'success';
  return 'default';
}

function scopeLabel(scope: StandardScopeType): string {
  if (scope === 'stock_code') return 'stock code';
  if (scope === 'substrate') return 'raw material';
  if (scope === 'forme') return 'forme';
  if (scope === 'customer') return 'customer';
  return scope;
}

// Human label for the scope's value column header within a row.
function scopeValueLabel(scope: StandardScopeType): string {
  if (scope === 'default') return 'Press';
  if (scope === 'stock_code') return 'Stock code';
  if (scope === 'substrate') return 'Raw material';
  if (scope === 'forme') return 'Forme';
  if (scope === 'customer') return 'Customer';
  return scope;
}

function scopeDetail(scope: StandardScopeType): string {
  if (scope === 'default') return 'baseline used when no override matches';
  if (scope === 'forme') return 'applies to all jobs using this forme';
  if (scope === 'stock_code') return 'applies to one exact stock code';
  if (scope === 'substrate') return 'applies to all stock codes on this raw material';
  if (scope === 'customer') return 'applies to all jobs for this customer';
  return '';
}

interface NewForm {
  press_code: string;
  scope_type: 'stock_code' | 'substrate' | 'forme' | 'customer';
  scope_value: string;
  setup_minutes: string;
  run_rate_per_hour: string;
  washup_minutes: string;
  notes: string;
}

const EMPTY_FORM: NewForm = {
  press_code: DEFAULT_PRESS,
  scope_type: 'stock_code',
  scope_value: '',
  setup_minutes: '30',
  run_rate_per_hour: '15000',
  washup_minutes: '15',
  notes: '',
};

// Scope display order within a press section (cascade: most → least specific).
const SCOPE_ORDER: StandardScopeType[] = ['forme', 'stock_code', 'substrate', 'customer', 'default'];

export default function StandardsView(): JSX.Element {
  const queryClient = useQueryClient();
  const [pressFilter, setPressFilter] = useState<PressFilter>('all');
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all');
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<NewForm>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  const standardsQuery = useQuery({
    queryKey: STANDARDS_QUERY_KEY,
    queryFn: () => listStandards(),
  });
  const standards = standardsQuery.data ?? [];

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: STANDARDS_QUERY_KEY });

  // Distinct presses present in the data, in board order (P15, P11, then any
  // others alphabetically). Drives both the filter dropdown and the dialog picker.
  const presses = useMemo(() => {
    const set = Array.from(new Set(standards.map((s) => s.press_code)));
    // Ensure the known presses always appear even before their rows load.
    for (const p of PRESS_ORDER) if (!set.includes(p)) set.push(p);
    return set.sort((a, b) => pressRank(a) - pressRank(b) || a.localeCompare(b));
  }, [standards]);

  const upsertMutation = useMutation({
    mutationFn: upsertStandard,
    onError: (e: any) => setError(e?.response?.data?.error ?? e?.message ?? 'Save failed'),
    onSuccess: () => {
      setDialogOpen(false);
      setForm(EMPTY_FORM);
    },
    onSettled: () => invalidate(),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Partial<ProductionStandard> }) =>
      updateStandard(id, patch),
    onError: (e: any) => setError(e?.response?.data?.error ?? e?.message ?? 'Update failed'),
    onSettled: () => invalidate(),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteStandard,
    onError: (e: any) => setError(e?.response?.data?.error ?? e?.message ?? 'Delete failed'),
    onSettled: () => invalidate(),
  });

  const filtered = useMemo(() => {
    let result = standards;
    if (pressFilter !== 'all') result = result.filter((s) => s.press_code === pressFilter);
    if (scopeFilter !== 'all') result = result.filter((s) => s.scope_type === scopeFilter);
    const s = search.trim().toLowerCase();
    if (s) {
      result = result.filter(
        (st) =>
          (st.scope_value ?? '').toLowerCase().includes(s) ||
          (st.notes ?? '').toLowerCase().includes(s) ||
          st.press_code.toLowerCase().includes(s),
      );
    }
    return result;
  }, [standards, pressFilter, scopeFilter, search]);

  // Group filtered rows by press, then by scope so each press's cascade
  // (stock_code → substrate → default) is visible at a glance.
  const groupedByPress: {
    press: string;
    scopes: { scope: StandardScopeType; rows: ProductionStandard[] }[];
  }[] = useMemo(() => {
    const order = presses.filter((p) => pressFilter === 'all' || p === pressFilter);
    return order
      .map((press) => ({
        press,
        scopes: SCOPE_ORDER.map((scope) => ({
          scope,
          rows: filtered.filter((s) => s.press_code === press && s.scope_type === scope),
        })).filter((g) => g.rows.length > 0),
      }))
      .filter((g) => g.scopes.length > 0);
  }, [filtered, presses, pressFilter]);

  const handleAdd = () => {
    setError(null);
    const value = form.scope_value.trim();
    if (!value) {
      setError(`${scopeLabel(form.scope_type)} value is required`);
      return;
    }
    upsertMutation.mutate({
      press_code: form.press_code,
      scope_type: form.scope_type,
      scope_value: value,
      setup_minutes: parseInt(form.setup_minutes, 10) || 0,
      run_rate_per_hour: parseInt(form.run_rate_per_hour, 10) || 0,
      washup_minutes: parseInt(form.washup_minutes, 10) || 0,
      notes: form.notes.trim() || null,
    });
  };

  const handleFieldUpdate = (
    standard: ProductionStandard,
    field: 'setup_minutes' | 'run_rate_per_hour' | 'washup_minutes',
    value: string,
  ) => {
    const parsed = parseInt(value, 10);
    if (Number.isNaN(parsed) || parsed < 0) return;
    updateMutation.mutate({ id: standard.id, patch: { [field]: parsed } as any });
  };

  const handleDelete = (s: ProductionStandard) => {
    if (s.scope_type === 'default') return;
    if (
      !window.confirm(
        `Delete ${scopeLabel(s.scope_type)} override for ${s.scope_value} on ${s.press_code}?`,
      )
    )
      return;
    deleteMutation.mutate(s.id);
  };

  // A representative default row for the worked-example footer (units sanity check).
  const defaultRow = standards.find((s) => s.scope_type === 'default');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(135deg, rgba(203,96,21,0.05) 0%, rgba(203,96,21,0.01) 100%)',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              display: 'inline-flex',
            }}
          >
            <SpeedIcon />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Speeds &amp; Standards
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              Make-ready, run-rate, and wash-up times the scheduler uses to plan job duration,
              per press. Resolution cascade (highest priority first):{' '}
              <strong>stock code → raw material → default</strong>.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setForm({
                ...EMPTY_FORM,
                press_code: pressFilter === 'all' ? DEFAULT_PRESS : pressFilter,
              });
              setError(null);
              setDialogOpen(true);
            }}
            data-testid="new-standard-btn"
          >
            New override
          </Button>
        </Stack>
      </Paper>

      {error && !dialogOpen && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filter bar */}
      <Paper
        elevation={0}
        sx={{ p: { xs: 2, md: 2 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            select
            label="Press"
            value={pressFilter}
            onChange={(e) => setPressFilter(e.target.value as PressFilter)}
            size="small"
            sx={{ minWidth: 160 }}
            data-testid="press-filter"
          >
            <MenuItem value="all">All presses</MenuItem>
            {presses.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Scope"
            value={scopeFilter}
            onChange={(e) => setScopeFilter(e.target.value as ScopeFilter)}
            size="small"
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="forme">Forme</MenuItem>
            <MenuItem value="default">Default</MenuItem>
            <MenuItem value="substrate">Raw material</MenuItem>
            <MenuItem value="stock_code">Stock code</MenuItem>
            <MenuItem value="customer">Customer</MenuItem>
          </TextField>
          <TextField
            label="Search press, stock code, raw material, or notes"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            fullWidth
          />
        </Stack>
      </Paper>

      {/* Standards grouped by press, then scope */}
      {standardsQuery.isLoading ? (
        <Paper
          elevation={0}
          sx={{ p: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}
        >
          <CircularProgress size={20} />
        </Paper>
      ) : groupedByPress.length === 0 ? (
        <Paper
          elevation={0}
          sx={{ p: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No standards match these filters. Click <strong>New override</strong> to create one.
          </Typography>
        </Paper>
      ) : (
        groupedByPress.map(({ press, scopes }) => (
          <Box key={press} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 0.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'ui-monospace, monospace' }}>
                {press}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {scopes.reduce((n, g) => n + g.rows.length, 0)}{' '}
                {scopes.reduce((n, g) => n + g.rows.length, 0) === 1 ? 'entry' : 'entries'}
              </Typography>
            </Stack>
            {scopes.map(({ scope, rows }) => (
              <Paper
                key={`${press}-${scope}`}
                elevation={0}
                sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}
              >
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ px: 2, py: 1.5, bgcolor: 'rgba(0,0,0,0.02)' }}
                >
                  <Chip
                    size="small"
                    label={scopeLabel(scope)}
                    color={scopeChipColour(scope)}
                    variant={scope === 'default' ? 'filled' : 'outlined'}
                  />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {scopeDetail(scope)} · {rows.length} {rows.length === 1 ? 'entry' : 'entries'}
                  </Typography>
                </Stack>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {scopeValueLabel(scope)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Make-ready
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Run rate
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Wash-up
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((s) => (
                      <TableRow key={s.id} hover data-testid={`standard-row-${s.id}`}>
                        <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>
                          {s.scope_type === 'default' ? s.press_code : s.scope_value || '—'}
                        </TableCell>
                        <TableCell align="right">
                          <InlineEdit
                            value={s.setup_minutes}
                            suffix="min"
                            onCommit={(v) => handleFieldUpdate(s, 'setup_minutes', v)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <InlineEdit
                            value={s.run_rate_per_hour}
                            suffix="sh/h"
                            onCommit={(v) => handleFieldUpdate(s, 'run_rate_per_hour', v)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <InlineEdit
                            value={s.washup_minutes}
                            suffix="min"
                            onCommit={(v) => handleFieldUpdate(s, 'washup_minutes', v)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {s.notes || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            disabled={s.scope_type === 'default'}
                            onClick={() => handleDelete(s)}
                            aria-label="Delete"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            ))}
          </Box>
        ))
      )}

      {/* Worked-example footer — keeps the units honest. */}
      {defaultRow && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Example ({defaultRow.press_code}): 11,050 sheets at{' '}
          {defaultRow.run_rate_per_hour.toLocaleString()} sh/h ={' '}
          {formatDuration(Math.ceil((11050 / defaultRow.run_rate_per_hour) * 60))} of running time.
        </Typography>
      )}

      {/* Add / new override dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New override</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {error && dialogOpen && <Alert severity="error">{error}</Alert>}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label="Press"
                value={form.press_code}
                onChange={(e) => setForm((f) => ({ ...f, press_code: e.target.value }))}
                size="small"
                sx={{ minWidth: 140 }}
                data-testid="new-standard-press"
              >
                {presses.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Scope"
                value={form.scope_type}
                onChange={(e) =>
                  setForm((f) => ({ ...f, scope_type: e.target.value as NewForm['scope_type'] }))
                }
                size="small"
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="forme">Forme</MenuItem>
                <MenuItem value="stock_code">Stock code</MenuItem>
                <MenuItem value="substrate">Raw material</MenuItem>
                <MenuItem value="customer">Customer</MenuItem>
              </TextField>
              <TextField
                label={
                  form.scope_type === 'stock_code' ? 'Stock code (exact)'
                    : form.scope_type === 'substrate' ? 'Raw material prefix'
                    : form.scope_type === 'forme' ? 'Forme number'
                    : 'Customer code'
                }
                value={form.scope_value}
                onChange={(e) => setForm((f) => ({ ...f, scope_value: e.target.value }))}
                size="small"
                fullWidth
                autoFocus
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Make-ready"
                type="number"
                value={form.setup_minutes}
                onChange={(e) => setForm((f) => ({ ...f, setup_minutes: e.target.value }))}
                size="small"
                fullWidth
                InputProps={{ endAdornment: <Typography variant="caption">min</Typography> }}
              />
              <TextField
                label="Run rate"
                type="number"
                value={form.run_rate_per_hour}
                onChange={(e) => setForm((f) => ({ ...f, run_rate_per_hour: e.target.value }))}
                size="small"
                fullWidth
                InputProps={{ endAdornment: <Typography variant="caption">sh/h</Typography> }}
              />
              <TextField
                label="Wash-up"
                type="number"
                value={form.washup_minutes}
                onChange={(e) => setForm((f) => ({ ...f, washup_minutes: e.target.value }))}
                size="small"
                fullWidth
                InputProps={{ endAdornment: <Typography variant="caption">min</Typography> }}
              />
            </Stack>
            <TextField
              label="Notes (optional)"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAdd}
            variant="contained"
            disabled={upsertMutation.isPending || !form.scope_value.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function InlineEdit({
  value,
  suffix,
  onCommit,
}: {
  value: number;
  suffix: string;
  onCommit: (v: string) => void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  if (editing) {
    return (
      <TextField
        size="small"
        type="number"
        value={draft}
        autoFocus
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          setEditing(false);
          if (draft !== String(value)) onCommit(draft);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') {
            setDraft(String(value));
            setEditing(false);
          }
        }}
        sx={{ width: 100 }}
        inputProps={{ style: { textAlign: 'right' } }}
      />
    );
  }
  return (
    <Box
      onClick={() => setEditing(true)}
      sx={{
        cursor: 'pointer',
        display: 'inline-block',
        px: 1,
        py: 0.5,
        borderRadius: 1,
        '&:hover': { backgroundColor: 'action.hover' },
      }}
    >
      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
        {value.toLocaleString()} {suffix}
      </Typography>
    </Box>
  );
}
